import { useMemo, useState } from 'react';
import type { CrmContact } from '@/lib/crm-account';
import type { AccountFact } from '@/lib/account-knowledge';
import { AccountFactRow, shortDate } from './AccountFactRow';

const key = (name: string) => name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();

interface CitedPerson {
  name: string;
  role: string | null;
  facts: AccountFact[];
}

/** People named in the account's active facts, newest first; a name is a label here, never an identity. */
export const citedPeople = (facts: AccountFact[]): CitedPerson[] => {
  const byName = new Map<string, CitedPerson>();
  [...facts].filter((fact) => fact.state === 'active' && fact.person)
    .sort((left, right) => right.observedAt.localeCompare(left.observedAt))
    .forEach((fact) => {
      const person = fact.person!;
      const entry = byName.get(key(person.name)) ?? { name: person.name, role: null, facts: [] };
      entry.role = entry.role ?? person.role;
      entry.facts.push(fact);
      byName.set(key(person.name), entry);
    });
  return [...byName.values()];
};

/**
 * « Personnes » (26/10/2026): who is known at the account. HubSpot contacts
 * first (exact), then the people the reader found named in what it learned,
 * each with the facts that name them. Nothing shows when nobody is known.
 */
export const AccountPeople = ({ contacts, facts, accountSubjectId, nameOf, onRemove }: {
  contacts: CrmContact[];
  facts: AccountFact[];
  accountSubjectId: string;
  nameOf: (userId: string) => string | null;
  onRemove: (fact: AccountFact) => void;
}) => {
  const [open, setOpen] = useState<string | null>(null);
  const contactNames = useMemo(() => new Set(contacts.flatMap((contact) => contact.name ? [key(contact.name)] : [])), [contacts]);
  const cited = useMemo(() => citedPeople(facts), [facts]);
  const citedByName = useMemo(() => new Map(cited.map((person) => [key(person.name), person])), [cited]);
  const others = cited.filter((person) => !contactNames.has(key(person.name)));
  if (contacts.length === 0 && others.length === 0) return null;

  const factsOf = (person: CitedPerson) => (
    <ul className="mt-2 rounded-xl bg-domino-muted/60 motion-safe:animate-fadeIn">
      {person.facts.map((fact) => <AccountFactRow key={fact.id} fact={fact} accountSubjectId={accountSubjectId} nameOf={nameOf} onRemove={onRemove} />)}
    </ul>
  );
  const toggle = (name: string) => setOpen((current) => current === key(name) ? null : key(name));
  const factsButton = (person: CitedPerson) => (
    <button type="button" aria-expanded={open === key(person.name)} onClick={() => toggle(person.name)}
      className="shrink-0 text-xs text-domino-aubergine underline-offset-2 hover:underline">
      {person.facts.length} fait{person.facts.length > 1 ? 's' : ''}
    </button>
  );

  return (
    <section className="mb-8" aria-labelledby="account-people-title">
      <h2 id="account-people-title" className="mb-3 text-lg font-semibold text-domino-ink">Personnes</h2>
      <ul className="divide-y divide-domino-cardBorder rounded-2xl border border-domino-cardBorder bg-domino-white">
        {contacts.map((contact) => {
          const person = contact.name ? citedByName.get(key(contact.name)) : undefined;
          const details = [contact.jobTitle ?? person?.role, contact.email, 'HubSpot'].filter(Boolean).join(' · ');
          return (
            <li key={`c-${contact.id}`} className="px-5 py-3 sm:px-6">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-domino-ink">{contact.name ?? contact.email ?? 'Contact sans nom'}</p>
                  <p className="truncate text-xs text-domino-inkSoft">{details}</p>
                </div>
                {person && factsButton(person)}
              </div>
              {person && open === key(person.name) && factsOf(person)}
            </li>
          );
        })}
        {others.map((person) => (
          <li key={`p-${key(person.name)}`} className="px-5 py-3 sm:px-6">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-domino-ink">{person.name}</p>
                <p className="truncate text-xs text-domino-inkSoft">
                  {[person.role, `cité le ${shortDate(person.facts[0]!.observedAt)}`].filter(Boolean).join(' · ')}
                </p>
              </div>
              {factsButton(person)}
            </div>
            {open === key(person.name) && factsOf(person)}
          </li>
        ))}
      </ul>
    </section>
  );
};
